import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import {
    ButtonSkeleton,
    Card,
    Description,
    Heading,
    type OverlayTriggerState,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import type { LoadingStateHookResult } from '@gitbook/util-react-hooks';

import { useNavigationState } from '../../../navigation';
import { useEvaluateMergeRules, useSpaceMergeRules } from '../../MergeRules';
import { ChangeRequestMergeButton } from '../ChangeRequestMergeButtons';
import { useChangeRequestMergeActions } from '../ChangeRequestMergeProvider';
import {
    ChangeRequestMergeRulesListTitle,
    ChangeRequestMergeRulesStatusList,
    getMergeRulesStatusDescription,
} from '../ChangeRequestMergeRulesStatus';
import { ChangeRequestOutOfDateButton } from '../ChangeRequestOutOfDate';
import { WithChangeRequestRevision } from '../Changes';

export type ChangeRequestMergeBlockProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
};

export function ChangeRequestMergeBlock(props: ChangeRequestMergeBlockProps) {
    return (
        <WithChangeRequestRevision {...props}>
            <React.Suspense fallback={null}>
                <ChangeRequestMergeBlockWithData {...props} />
            </React.Suspense>
        </WithChangeRequestRevision>
    );
}

function ChangeRequestMergeBlockWithData(props: ChangeRequestMergeBlockProps) {
    const { space, changeRequest } = props;
    const mergeRules = useSpaceMergeRules(space);
    const mergeRulesEvaluation = useEvaluateMergeRules({ space, changeRequest });
    const status = getMergeRulesStatusDescription({ space, mergeRules, mergeRulesEvaluation });

    const confirmOutdatedMergeDialogTrigger = useDialogTriggerState();
    const {
        isOutOfDate,
        mergeLoadable,
        updateLoadable,
        updateChangeRequest,
        mergeChangeRequest,
        onMerge: handleMerge,
    } = useChangeRequestMergeActions();
    const [loadingUpdate] = updateLoadable;

    const onMerge = async () => {
        await handleMerge(confirmOutdatedMergeDialogTrigger);
    };

    // Show merge actions only when the CR is viewed in the dedicated ChangeRequestsScreen (as an overlay)
    // Hide them when viewing in SpaceScreen (as the main screen)
    const navigationState = useNavigationState();
    const showMergeActions = !!navigationState.overlay;

    return (
        <Card className="border-0">
            <div className="flex flex-col gap-2 px-5 py-3">
                <ChangeRequestMergeRulesListTitle space={space} status={status} />
                <Description>{status.description}</Description>
            </div>

            {mergeRulesEvaluation.rejectedRules.length > 0 ||
            mergeRulesEvaluation.passedRules.length > 0 ||
            mergeRulesEvaluation.skippedRules.length > 0 ? (
                <div className="flex flex-col gap-2 border-t bg-muted px-5 py-3">
                    <ChangeRequestMergeRulesStatusList
                        mergeRulesEvaluation={mergeRulesEvaluation}
                        buttonProps={{
                            kind: 'nested',
                        }}
                    />
                </div>
            ) : null}

            {isOutOfDate ? (
                <ChangeRequestMergeBlockOutOfDate
                    dialogTrigger={confirmOutdatedMergeDialogTrigger}
                    onUpdate={updateChangeRequest}
                    onMerge={mergeChangeRequest}
                    disabled={loadingUpdate}
                    loadable={updateLoadable}
                />
            ) : null}

            {showMergeActions ? (
                <div className="border-t px-5 py-3">
                    <React.Suspense
                        fallback={
                            // We need a suspense boundary as the evaluation of the merge rule can be async
                            <ButtonSkeleton label="Merge" />
                        }
                    >
                        <ChangeRequestMergeButton
                            {...props}
                            loadable={mergeLoadable}
                            disabled={loadingUpdate}
                            onMerge={onMerge}
                        />
                    </React.Suspense>
                </div>
            ) : null}
        </Card>
    );
}

function ChangeRequestMergeBlockOutOfDate(props: {
    dialogTrigger: OverlayTriggerState;
    onMerge: () => Promise<void>;
    onUpdate: () => Promise<void>;
    loadable: LoadingStateHookResult;
    disabled: boolean;
}) {
    const { onUpdate, onMerge, disabled, loadable, dialogTrigger } = props;

    return (
        <div className="flex items-center justify-between gap-6 border-t p-5">
            <div>
                <Heading>Change request out of date</Heading>
                <Description>
                    This change request is out of date with the space's primary content.
                </Description>
            </div>

            <ChangeRequestOutOfDateButton
                onUpdate={onUpdate}
                onMerge={onMerge}
                disabled={disabled}
                loadable={loadable}
                dialogTrigger={dialogTrigger}
                title={null}
            />
        </div>
    );
}
